import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Users, FolderKanban, FileText, Image, MapPin, Briefcase, Megaphone, Mail, Settings as SettingsIcon, ArrowLeft, Newspaper, UserCircle, Upload, X, Loader2, ZoomIn, ZoomOut, RotateCw, Crop, Mic2, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import Cropper from "react-easy-crop";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_bha-collective/artifacts/0ijxjfll_image.png";

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      sessionStorage.setItem("admin_token", res.data.token);
      onLogin(res.data.token);
      toast.success("Logged in successfully");
    } catch {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border border-slate-200 shadow-lg">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <div className="h-16 w-16 mx-auto rounded-full bg-[#0F172A] overflow-hidden flex items-center justify-center">
              <img src={LOGO_URL} alt="Texas BHA" className="object-contain mix-blend-screen" style={{ width: '280%', height: '280%', marginTop: '15%' }} />
            </div>
            <h1 className="text-2xl font-bold text-[#0F172A] mt-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Admin Panel</h1>
            <p className="text-slate-500 text-sm mt-1">Sign in to manage Texas BHA content</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
            <div>
              <Label className="text-sm font-medium text-slate-700">Email</Label>
              <Input data-testid="admin-email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter email" className="mt-1.5 bg-slate-50" required />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Password</Label>
              <Input data-testid="admin-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" className="mt-1.5 bg-slate-50" required />
            </div>
            <Button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full font-semibold">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
          <Link to="/" className="block text-center mt-4">
            <Button variant="ghost" size="sm" className="text-slate-500 text-xs">
              <ArrowLeft className="mr-1 h-3 w-3" /> Back to Website
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}

// Helper: create cropped image from canvas
function getCroppedImg(imageSrc, pixelCrop) {
  return new Promise((resolve) => {
    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
      );
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
    };
    image.src = imageSrc;
  });
}

// Image Upload Component with Crop
function ImageUploadField({ value, onChange, label }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || "");
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [aspect, setAspect] = useState(1);
  const fileInputRef = useRef(null);

  useEffect(() => { setPreview(value || ""); }, [value]);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp', 'image/heic', 'image/heif'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|heic|heif)$/i)) {
      toast.error("Please select a valid image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = async () => {
    if (!cropSrc || !croppedAreaPixels) return;
    setUploading(true);
    try {
      const blob = await getCroppedImg(cropSrc, croppedAreaPixels);
      const formData = new FormData();
      formData.append("file", blob, "cropped.jpg");
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${API.replace('/api', '')}${res.data.url}`;
      setPreview(fullUrl);
      onChange(fullUrl);
      setCropSrc(null);
      toast.success("Image uploaded successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview("");
    onChange("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div>
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <div className="mt-1.5">
        {preview ? (
          <div className="relative inline-block">
            <img src={preview} alt="Preview" className="h-32 w-32 object-cover rounded-lg border border-slate-200"
              onError={(e) => { e.target.src = "https://via.placeholder.com/128?text=Image"; }} />
            <button type="button" onClick={handleRemove}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div onClick={() => !uploading && fileInputRef.current?.click()}
            className={`w-full h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition-all ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
            {uploading ? (
              <><Loader2 className="h-8 w-8 text-slate-400 animate-spin" /><span className="text-sm text-slate-500">Uploading...</span></>
            ) : (
              <><Upload className="h-8 w-8 text-slate-400" /><span className="text-sm text-slate-500">Click to upload image</span><span className="text-xs text-slate-400">JPG, PNG, GIF, WebP (max 10MB)</span></>
            )}
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif" onChange={handleFileSelect} className="hidden" />
      </div>

      {/* Crop Dialog */}
      <Dialog open={!!cropSrc} onOpenChange={(open) => { if (!open) setCropSrc(null); }}>
        <DialogContent className="max-w-xl p-0 overflow-hidden" data-testid="crop-dialog">
          <DialogHeader className="px-5 pt-5 pb-2">
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Crop className="h-4 w-4" /> Crop & Adjust Image
            </DialogTitle>
          </DialogHeader>
          <div className="relative w-full h-[360px] bg-slate-900">
            {cropSrc && (
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                showGrid={true}
              />
            )}
          </div>
          <div className="px-5 py-3 space-y-3 border-t border-slate-100">
            {/* Zoom */}
            <div className="flex items-center gap-3">
              <ZoomOut className="h-4 w-4 text-slate-400 shrink-0" />
              <input type="range" min={1} max={3} step={0.05} value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 h-1.5 accent-[#0F172A] cursor-pointer" data-testid="crop-zoom-slider" />
              <ZoomIn className="h-4 w-4 text-slate-400 shrink-0" />
            </div>
            {/* Aspect Ratio */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-medium mr-1">Ratio:</span>
              {[
                { label: "Free", val: undefined },
                { label: "1:1", val: 1 },
                { label: "16:9", val: 16 / 9 },
                { label: "4:3", val: 4 / 3 },
                { label: "3:2", val: 3 / 2 },
              ].map((r) => (
                <button key={r.label} onClick={() => setAspect(r.val)}
                  data-testid={`crop-ratio-${r.label.replace(/[:/]/g, '')}`}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                    aspect === r.val ? "bg-[#0F172A] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter className="px-5 pb-5 pt-2">
            <Button variant="outline" onClick={() => setCropSrc(null)} className="rounded-full text-sm">Cancel</Button>
            <Button onClick={handleCropConfirm} disabled={uploading}
              className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full text-sm font-semibold"
              data-testid="crop-confirm-btn">
              {uploading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Uploading...</> : "Crop & Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CrudFormField({ field, value, onChange }) {
  if (field.type === "image") {
    return <ImageUploadField label={field.label} value={value} onChange={onChange} />;
  }
  if (field.type === "textarea") {
    return (
      <div>
        <Label className="text-sm font-medium text-slate-700">{field.label}</Label>
        <Textarea data-testid={`form-${field.key}`} value={value} onChange={e => onChange(e.target.value)}
          className="mt-1.5 bg-slate-50" rows={3} />
        {field.hint && <p className="text-xs text-slate-400 mt-1">{field.hint}</p>}
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        <Label className="text-sm font-medium text-slate-700">{field.label}</Label>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="mt-1.5 bg-slate-50"><SelectValue /></SelectTrigger>
          <SelectContent>
            {field.options?.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
        {field.hint && <p className="text-xs text-slate-400 mt-1">{field.hint}</p>}
      </div>
    );
  }
  return (
    <div>
      <Label className="text-sm font-medium text-slate-700">{field.label}</Label>
      <Input data-testid={`form-${field.key}`} type={field.type === "number" ? "number" : "text"}
        value={value} onChange={e => onChange(e.target.value)}
        className="mt-1.5 bg-slate-50" placeholder={field.placeholder || ""} />
      {field.hint && <p className="text-xs text-slate-400 mt-1">{field.hint}</p>}
    </div>
  );
}

function CrudFormDialog({ open, onOpenChange, title, editing, fields, formData, setFormData, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editing ? `Edit ${title}` : `New ${title}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {fields.map(f => (
            <CrudFormField key={f.key} field={f} value={formData[f.key] || ""}
              onChange={(val) => setFormData(prev => ({ ...prev, [f.key]: val }))} />
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full text-sm">Cancel</Button>
          <Button data-testid="form-save-btn" onClick={onSave} className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full text-sm font-semibold">
            {editing ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CrudItemList({ items, resource, onEdit, onDelete }) {
  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div key={item.id || idx} data-testid={`${resource}-item-${idx}`} className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-xl hover:shadow-sm transition-shadow">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-[#0F172A] text-sm truncate">{item.name || item.title}</p>
            <p className="text-slate-400 text-xs truncate mt-0.5">{item.description || item.summary || item.content || ""}</p>
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            <Button data-testid={`edit-${resource}-${idx}`} variant="ghost" size="icon" onClick={() => onEdit(item)} className="h-8 w-8">
              <Pencil className="h-3.5 w-3.5 text-slate-500" />
            </Button>
            <Button data-testid={`delete-${resource}-${idx}`} variant="ghost" size="icon" onClick={() => onDelete(item.id)} className="h-8 w-8">
              <Trash2 className="h-3.5 w-3.5 text-red-500" />
            </Button>
          </div>
        </div>
      ))}
      {items.length === 0 && (
        <p className="text-slate-400 text-sm text-center py-8">No items yet. Click "Add New" to create one.</p>
      )}
    </div>
  );
}

function CrudSection({ title, resource, fields, token, icon: Icon }) {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/${resource}`);
      setItems(res.data);
    } catch (err) {
      console.error(`Failed to fetch ${resource}:`, err.message);
    }
  }, [resource]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openNew = () => {
    setEditing(null);
    const defaults = {};
    fields.forEach(f => { defaults[f.key] = f.defaultValue || ""; });
    setFormData(defaults);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    const data = {};
    fields.forEach(f => {
      data[f.key] = Array.isArray(item[f.key]) ? item[f.key].join(", ") : (item[f.key] || "");
    });
    setFormData(data);
    setDialogOpen(true);
  };

  const buildPayload = () => {
    const payload = {};
    fields.forEach(f => {
      if (f.type === "array") {
        payload[f.key] = formData[f.key] ? formData[f.key].split(",").map(s => s.trim()).filter(Boolean) : [];
      } else if (f.type === "number" || f.key === "lat" || f.key === "lng") {
        payload[f.key] = parseFloat(formData[f.key]) || 0;
      } else if (f.type === "boolean") {
        payload[f.key] = formData[f.key] === "true" || formData[f.key] === true;
      } else {
        payload[f.key] = formData[f.key] || "";
      }
    });
    return payload;
  };

  const handleSave = async () => {
    try {
      const payload = buildPayload();
      if (editing) {
        await axios.put(`${API}/admin/${resource}/${editing.id}`, payload, { headers });
        toast.success(`${title} updated`);
      } else {
        await axios.post(`${API}/admin/${resource}`, payload, { headers });
        toast.success(`${title} created`);
      }
      setDialogOpen(false);
      fetchItems();
    } catch {
      toast.error("Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await axios.delete(`${API}/admin/${resource}/${id}`, { headers });
      toast.success("Deleted successfully");
      fetchItems();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-[#0F172A]" />
          <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}s</h2>
          <Badge variant="secondary" className="text-xs">{items.length}</Badge>
        </div>
        <Button data-testid={`add-${resource}-btn`} onClick={openNew} size="sm" className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full text-xs font-semibold">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add New
        </Button>
      </div>

      <CrudItemList items={items} resource={resource} onEdit={openEdit} onDelete={handleDelete} />

      <CrudFormDialog open={dialogOpen} onOpenChange={setDialogOpen} title={title} editing={editing}
        fields={fields} formData={formData} setFormData={setFormData} onSave={handleSave} />
    </div>
  );
}

function ContactsView({ token }) {
  const [contacts, setContacts] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/contacts`, { headers }).then(r => setContacts(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers });
      setContacts(contacts.filter(c => c.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Mail className="h-5 w-5 text-[#0F172A]" />
        <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Contact Submissions</h2>
        <Badge variant="secondary" className="text-xs">{contacts.length}</Badge>
      </div>
      <div className="space-y-3">
        {contacts.map((c, idx) => (
          <div key={c.id || idx} data-testid={`contact-item-${idx}`} className="p-4 bg-white border border-slate-100 rounded-xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-[#0F172A] text-sm">{c.name}</p>
                  <Badge variant="outline" className="text-xs">{c.inquiry_type}</Badge>
                </div>
                <p className="text-slate-400 text-xs mt-0.5">{c.email} {c.subject && `- ${c.subject}`}</p>
                <p className="text-slate-600 text-sm mt-2">{c.message}</p>
                <p className="text-slate-300 text-xs mt-2">{new Date(c.created_at).toLocaleString()}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} className="h-8 w-8 shrink-0">
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </Button>
            </div>
          </div>
        ))}
        {contacts.length === 0 && <p className="text-slate-400 text-sm text-center py-8">No contact submissions yet.</p>}
      </div>
    </div>
  );
}


// Page Editor: combines site images + content + CRUD for a page
function PageEditor({ title, token, imageKeys, contentKeys, crudSections }) {
  const [images, setImages] = useState({});
  const [content, setContent] = useState({});
  const [savingImg, setSavingImg] = useState(false);
  const [savingContent, setSavingContent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/site-images`).then(r => setImages(r.data || {})).catch(() => {});
    axios.get(`${API}/site-content`).then(r => setContent(r.data || {})).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageChange = (key, url) => {
    setImages(prev => ({ ...prev, [key]: url }));
  };

  const handleContentChange = (key, val) => {
    setContent(prev => ({ ...prev, [key]: val }));
  };

  const handleSaveImages = async () => {
    setSavingImg(true);
    try {
      await axios.put(`${API}/admin/site-images`, images, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Images saved!");
    } catch { toast.error("Failed to save images"); }
    finally { setSavingImg(false); }
  };

  const handleSaveContent = async () => {
    setSavingContent(true);
    try {
      await axios.put(`${API}/admin/site-content`, content, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Content saved!");
    } catch { toast.error("Failed to save content"); }
    finally { setSavingContent(false); }
  };

  return (
    <div className="space-y-8">
      {/* Page Content */}
      {contentKeys && contentKeys.length > 0 && (
        <Card className="border border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm text-[#0F172A] uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <FileText className="h-4 w-4 inline mr-2 text-slate-400" />{title} Page Content
              </h3>
              <Button onClick={handleSaveContent} disabled={savingContent} size="sm" className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full text-xs font-semibold">
                {savingContent ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Saving...</> : "Save Content"}
              </Button>
            </div>
            <div className="space-y-4">
              {contentKeys.map((item) => {
                if (item.type === "divider") {
                  return (
                    <div key={item.key} className="pt-4 pb-1 border-t border-slate-200 mt-2">
                      <p className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">{item.label.replace(/─/g, '').trim()}</p>
                    </div>
                  );
                }
                return (
                  <div key={item.key}>
                    <Label className="text-xs font-medium text-slate-500 uppercase tracking-wider">{item.label}</Label>
                    {item.type === "textarea" ? (
                      <Textarea value={content[item.key] || ""} onChange={e => handleContentChange(item.key, e.target.value)}
                        className="mt-1 bg-slate-50 text-sm" rows={3} placeholder={item.placeholder || ""} />
                    ) : (
                      <Input value={content[item.key] || ""} onChange={e => handleContentChange(item.key, e.target.value)}
                        className="mt-1 bg-slate-50 text-sm" placeholder={item.placeholder || ""} />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Page Images */}
      {imageKeys && imageKeys.length > 0 && (
        <Card className="border border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm text-[#0F172A] uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Image className="h-4 w-4 inline mr-2 text-slate-400" />{title} Page Images
              </h3>
              <Button onClick={handleSaveImages} disabled={savingImg} size="sm" className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full text-xs font-semibold">
                {savingImg ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Saving...</> : "Save Images"}
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {imageKeys.map((item) => (
                <ImageUploadField key={item.key} label={item.label} value={images[item.key] || ""} onChange={(url) => handleImageChange(item.key, url)} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* CRUD Sections */}
      {crudSections && crudSections.map((section, i) => (
        <CrudSection key={i} title={section.title} resource={section.resource} fields={section.fields} token={token} icon={section.icon} />
      ))}
    </div>
  );
}

// Field definitions for each resource
const committeeFields = [
  { key: "name", label: "Name", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "mission", label: "Mission", type: "textarea" },
  { key: "leadership", label: "Leadership", type: "array", hint: "Comma-separated names" },
  { key: "photo_url", label: "Photo", type: "image" },
  { key: "order", label: "Display Order", type: "number", defaultValue: "0" },
];

const projectFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "image_url", label: "Project Image", type: "image" },
  { key: "status", label: "Status", type: "select", options: ["ongoing", "completed"] },
  { key: "outcome", label: "Outcome/Impact", type: "textarea" },
  { key: "tags", label: "Tags", type: "array", hint: "Comma-separated tags" },
];

const policyFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "summary", label: "Summary", type: "textarea" },
  { key: "status", label: "Status", type: "select", options: ["Active Research", "Data Collection", "Policy Brief Published", "In Progress"] },
  { key: "category", label: "Category", type: "text" },
  { key: "tags", label: "Tags", type: "array", hint: "Comma-separated" },
];

const galleryFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "image_url", label: "Image", type: "image" },
  { key: "category", label: "Category", type: "select", options: ["Events", "Meetings", "Socials", "Conferences"] },
  { key: "date", label: "Date", type: "text", placeholder: "YYYY-MM-DD" },
];

const chapterFields = [
  { key: "name", label: "Chapter Name", type: "text" },
  { key: "university", label: "University / School", type: "text" },
  { key: "location", label: "Location (City, TX)", type: "text" },
  { key: "chapter_type", label: "Chapter Type", type: "select", options: ["college", "high_school"] },
  { key: "lat", label: "Latitude", type: "text", placeholder: "e.g. 30.2849" },
  { key: "lng", label: "Longitude", type: "text", placeholder: "e.g. -97.7341" },
  { key: "founding_date", label: "Founding Date", type: "text", placeholder: "YYYY-MM-DD" },
  { key: "leadership", label: "Leadership", type: "array", hint: "Comma-separated" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "signup_link", label: "Sign-up Link", type: "text" },
];

const opportunityFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "type", label: "Type", type: "select", options: ["philanthropy", "internship", "volunteer", "sponsorship", "competition", "professional_development", "general"] },
  { key: "cta_text", label: "CTA Button Text", type: "text", defaultValue: "Learn More" },
  { key: "cta_link", label: "CTA Link", type: "text" },
];

const announcementFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "content", label: "Content", type: "textarea" },
  { key: "active", label: "Active", type: "select", options: ["true", "false"] },
];

const consultingFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "icon", label: "Icon", type: "select", options: ["target", "settings", "bar-chart", "shield", "cpu", "search"] },
];

const newsletterFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "date", label: "Date", type: "text", placeholder: "YYYY-MM-DD" },
  { key: "preview", label: "Preview Text", type: "textarea" },
  { key: "link", label: "Link/Download URL", type: "text" },
  { key: "category", label: "Type", type: "select", options: ["current", "past"] },
];

const teamFields = [
  { key: "name", label: "Name", type: "text" },
  { key: "role", label: "Role/Title", type: "text" },
  { key: "category", label: "Category", type: "select", options: ["Executive Board", "Marketing Chair", "Expansion Chair", "Committee Leads"] },
  { key: "bio", label: "Bio", type: "textarea" },
  { key: "photo_url", label: "Photo", type: "image" },
  { key: "order", label: "Display Order", type: "number", defaultValue: "0" },
];

const caseCompDocFields = [
  { key: "title", label: "Document Title", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "file_url", label: "File URL (upload via Images or paste link)", type: "text" },
];

const chatbotKnowledgeFields = [
  { key: "topic", label: "Topic (e.g. 'Membership Drive Spring 2026')", type: "text" },
  { key: "content", label: "Knowledge Content (the chatbot will use this to answer questions)", type: "textarea" },
];

const eventFields = [
  { key: "title", label: "Event Title", type: "text" },
  { key: "date", label: "Date", type: "text", placeholder: "e.g. March 28, 2025" },
  { key: "time", label: "Time", type: "text", placeholder: "e.g. 9:00 AM - 5:00 PM" },
  { key: "location", label: "Location", type: "text", placeholder: "e.g. UT Austin, McCombs School of Business" },
  { key: "description", label: "Description", type: "textarea" },
];

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("admin_token"));

  useEffect(() => {
    if (token) {
      axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => {
          sessionStorage.removeItem("admin_token");
          setToken(null);
        });
    }
  }, [token]);

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    setToken(null);
    toast.success("Logged out");
  };

  if (!token) return <AdminLogin onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-[#0F172A] overflow-hidden flex items-center justify-center">
              <img src={LOGO_URL} alt="Texas BHA" className="object-contain mix-blend-screen" style={{ width: '280%', height: '280%', marginTop: '15%' }} />
            </div>
            <span className="font-bold text-[#0F172A] text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Texas BHA <span className="text-slate-400 font-normal">Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-to-site">
              <Button variant="ghost" size="sm" className="text-slate-500 text-xs">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Site
              </Button>
            </Link>
            <Button data-testid="admin-logout-btn" variant="ghost" size="sm" onClick={handleLogout} className="text-red-500 hover:text-red-600 text-xs">
              <LogOut className="h-3.5 w-3.5 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Admin Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
        <h1 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Content Management</h1>
        <Tabs defaultValue="consulting" className="w-full">
          <TabsList className="flex flex-wrap gap-1 bg-white border border-slate-200 p-1 rounded-xl mb-8 h-auto">
            <TabsTrigger value="consulting" data-testid="tab-consulting" className="text-xs rounded-lg"><FolderKanban className="h-3.5 w-3.5 mr-1" />Consulting</TabsTrigger>
            <TabsTrigger value="policy" data-testid="tab-policies" className="text-xs rounded-lg"><FileText className="h-3.5 w-3.5 mr-1" />Policy</TabsTrigger>
            <TabsTrigger value="expansion" data-testid="tab-chapters" className="text-xs rounded-lg"><MapPin className="h-3.5 w-3.5 mr-1" />Expansion</TabsTrigger>
            <TabsTrigger value="outreach" data-testid="tab-outreach" className="text-xs rounded-lg"><Megaphone className="h-3.5 w-3.5 mr-1" />Outreach</TabsTrigger>
            <TabsTrigger value="philanthropy" data-testid="tab-philanthropy" className="text-xs rounded-lg"><Users className="h-3.5 w-3.5 mr-1" />Philanthropy</TabsTrigger>
            <TabsTrigger value="homepage" data-testid="tab-homepage" className="text-xs rounded-lg"><Image className="h-3.5 w-3.5 mr-1" />Homepage</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team" className="text-xs rounded-lg"><UserCircle className="h-3.5 w-3.5 mr-1" />Team</TabsTrigger>
            <TabsTrigger value="newsletters" data-testid="tab-newsletters" className="text-xs rounded-lg"><Newspaper className="h-3.5 w-3.5 mr-1" />Newsletters</TabsTrigger>
            <TabsTrigger value="conference" data-testid="tab-conference" className="text-xs rounded-lg"><Mic2 className="h-3.5 w-3.5 mr-1" />Case Comp</TabsTrigger>
            <TabsTrigger value="member-resources" data-testid="tab-member-resources" className="text-xs rounded-lg"><Briefcase className="h-3.5 w-3.5 mr-1" />Member Resources</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="text-xs rounded-lg"><Mail className="h-3.5 w-3.5 mr-1" />Contacts</TabsTrigger>
            <TabsTrigger value="chatbot" data-testid="tab-chatbot" className="text-xs rounded-lg"><Mic2 className="h-3.5 w-3.5 mr-1" />Chatbot</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="text-xs rounded-lg"><BarChart3 className="h-3.5 w-3.5 mr-1" />Analytics</TabsTrigger>
          </TabsList>

          {/* Consulting — Content + Images + Projects + Services */}
          <TabsContent value="consulting">
            <PageEditor title="Consulting" token={token}
              contentKeys={[
                { key: "consultingHeaderDesc", label: "Page Header Description", type: "textarea" },
                { key: "consultingMissionTitle", label: "Mission Section Title" },
                { key: "consultingMissionText", label: "Mission Section Text", type: "textarea" },
                { key: "consultingAppLink", label: "Application Link (URL)" },
                { key: "consultingAppDue", label: "Application Due Date" },
              ]}
              imageKeys={[
                { key: "consultingHero", label: "Team Photo" },
                { key: "consultingBanner", label: "Banner Photo" },
                { key: "consultingGallery1", label: "Gallery 1" },
                { key: "consultingGallery2", label: "Gallery 2" },
                { key: "consultingGallery3", label: "Gallery 3" },
                { key: "consultingGallery4", label: "Gallery 4" },
              ]}
              crudSections={[
                { title: "Project", resource: "projects", fields: projectFields, icon: FolderKanban },
                { title: "Consulting Service", resource: "consulting-services", fields: consultingFields, icon: SettingsIcon },
              ]}
            />
          </TabsContent>

          {/* Policy — Content + Images + Policies */}
          <TabsContent value="policy">
            <PageEditor title="Policy & Research" token={token}
              contentKeys={[
                { key: "policyHeaderDesc", label: "Page Header Description", type: "textarea" },
                { key: "policyMissionTitle", label: "Research Section Title" },
                { key: "policyMissionText", label: "Research Section Text", type: "textarea" },
                { key: "policyAppLink", label: "Application Link (URL)" },
                { key: "policyAppDue", label: "Application Due Date" },
              ]}
              imageKeys={[
                { key: "policyHero", label: "Hero Image" },
                { key: "policyBanner", label: "Banner Photo" },
                { key: "policyGallery1", label: "Gallery 1" },
                { key: "policyGallery2", label: "Gallery 2" },
                { key: "policyGallery3", label: "Gallery 3" },
                { key: "policyGallery4", label: "Gallery 4" },
              ]}
              crudSections={[
                { title: "Policy", resource: "policies", fields: policyFields, icon: FileText },
              ]}
            />
          </TabsContent>

          {/* Expansion — Content + Images + Chapters */}
          <TabsContent value="expansion">
            <PageEditor title="Expansion" token={token}
              contentKeys={[
                { key: "chaptersHeaderDesc", label: "Page Header Description", type: "textarea" },
                { key: "chaptersTeamDesc", label: "Team Section Description", type: "textarea" },
                { key: "chaptersMapTitle", label: "Map Section Title" },
                { key: "chaptersMapDesc", label: "Map Section Description", type: "textarea" },
                { key: "chaptersCTATitle", label: "CTA Section Title" },
                { key: "chaptersCTAText", label: "CTA Section Text", type: "textarea" },
                { key: "expansionAppLink", label: "Application Link (URL)" },
                { key: "expansionAppDue", label: "Application Due Date" },
              ]}
              imageKeys={[
                { key: "chaptersTeamPhoto", label: "Team Photo" },
                { key: "chaptersBanner", label: "Banner Photo" },
                { key: "chaptersGallery1", label: "Gallery 1" },
                { key: "chaptersGallery2", label: "Gallery 2" },
                { key: "chaptersGallery3", label: "Gallery 3" },
                { key: "chaptersGallery4", label: "Gallery 4" },
              ]}
              crudSections={[
                { title: "Chapter", resource: "chapters", fields: chapterFields, icon: MapPin },
              ]}
            />
          </TabsContent>

          {/* Outreach — Content + Images + Events */}
          <TabsContent value="outreach">
            <PageEditor title="Outreach" token={token}
              contentKeys={[
                { key: "outreachHeaderDesc", label: "Page Header Description", type: "textarea" },
                { key: "outreachMissionTitle", label: "Mission Title" },
                { key: "outreachMissionText", label: "Mission Description", type: "textarea" },
                { key: "outreachBullets", label: "Bullet Points (comma-separated)", type: "textarea", placeholder: "Item 1,Item 2,Item 3" },
                { key: "outreachAppLink", label: "Application Link (URL)" },
                { key: "outreachAppDue", label: "Application Due Date" },
              ]}
              imageKeys={[
                { key: "outreachTeam", label: "Team Photo" },
                { key: "outreachGallery1", label: "Gallery 1" },
                { key: "outreachGallery2", label: "Gallery 2" },
                { key: "outreachGallery3", label: "Gallery 3" },
                { key: "outreachGallery4", label: "Gallery 4" },
              ]}
              crudSections={[
                { title: "Event", resource: "events", fields: eventFields, icon: Megaphone },
              ]}
            />
          </TabsContent>

          {/* Philanthropy — Content + Images */}
          <TabsContent value="philanthropy">
            <PageEditor title="Philanthropy" token={token}
              contentKeys={[
                { key: "philanthropyHeaderDesc", label: "Page Header Description", type: "textarea" },
                { key: "philanthropyMissionTitle", label: "Mission Title" },
                { key: "philanthropyMissionText", label: "Mission Description", type: "textarea" },
                { key: "philanthropyBullets", label: "Bullet Points (comma-separated)", type: "textarea", placeholder: "Item 1,Item 2,Item 3" },
                { key: "philanthropyInitiativesTitle", label: "Initiatives Section Title" },
                { key: "philanthropyInitiativesDesc", label: "Initiatives Section Description", type: "textarea" },
                { key: "philanthropyInitiative1Title", label: "Initiative 1 Title" },
                { key: "philanthropyInitiative1Desc", label: "Initiative 1 Description", type: "textarea" },
                { key: "philanthropyInitiative1Link", label: "Initiative 1 Sign-Up Link" },
                { key: "philanthropyInitiative2Title", label: "Initiative 2 Title" },
                { key: "philanthropyInitiative2Desc", label: "Initiative 2 Description", type: "textarea" },
                { key: "philanthropyInitiative2Link", label: "Initiative 2 Sign-Up Link" },
                { key: "philanthropyInitiative3Title", label: "Initiative 3 Title" },
                { key: "philanthropyInitiative3Desc", label: "Initiative 3 Description", type: "textarea" },
                { key: "philanthropyInitiative3Link", label: "Initiative 3 Sign-Up Link" },
                { key: "philanthropyCTATitle", label: "CTA Title" },
                { key: "philanthropyCTAText", label: "CTA Description", type: "textarea" },
              ]}
              imageKeys={[
                { key: "philanthropyHero", label: "Hero Image" },
                { key: "philanthropyBanner", label: "Banner Photo" },
                { key: "philanthropyGallery1", label: "Gallery 1" },
                { key: "philanthropyGallery2", label: "Gallery 2" },
                { key: "philanthropyGallery3", label: "Gallery 3" },
                { key: "philanthropyGallery4", label: "Gallery 4" },
              ]}
              crudSections={[]}
            />
          </TabsContent>

          {/* Homepage — Content + Images + Announcements + Committees */}
          <TabsContent value="homepage">
            <PageEditor title="Homepage" token={token}
              contentKeys={[
                { key: "homeHeroTitle", label: "Hero Title" },
                { key: "homeHeroSubtitle", label: "Hero Subtitle", type: "textarea" },
                { key: "homeAboutTitle", label: "About Section Title" },
                { key: "homeAboutText", label: "About Section Text", type: "textarea" },
                { key: "homeStatsMembers", label: "Stats: Members Count", placeholder: "500+" },
                { key: "homeStatsChapters", label: "Stats: Chapters Count", placeholder: "10" },
                { key: "homeStatsProjects", label: "Stats: Projects Count", placeholder: "25+" },
              ]}
              imageKeys={[
                { key: "homeHeroBg", label: "Hero Background" },
                { key: "homeAbout", label: "About Section Photo" },
                { key: "homeBanner", label: "Full-Width Banner" },
                { key: "homeDefaultCommittee", label: "Default Committee Photo" },
                { key: "homeGallery1", label: "Gallery Preview 1" },
                { key: "homeGallery2", label: "Gallery Preview 2" },
                { key: "homeGallery3", label: "Gallery Preview 3" },
                { key: "homeGallery4", label: "Gallery Preview 4" },
              ]}
              crudSections={[
                { title: "Announcement", resource: "announcements", fields: announcementFields, icon: Megaphone },
                { title: "Committee", resource: "committees", fields: committeeFields, icon: Users },
              ]}
            />
          </TabsContent>

          {/* Team + Content + Banner */}
          <TabsContent value="team">
            <PageEditor title="Team" token={token}
              contentKeys={[
                { key: "teamHeaderDesc", label: "Page Header Description", type: "textarea" },
                { key: "teamCTATitle", label: "CTA Title" },
                { key: "teamCTAText", label: "CTA Description", type: "textarea" },
              ]}
              imageKeys={[{ key: "teamBanner", label: "Team Banner Photo" }]}
              crudSections={[
                { title: "Team Member", resource: "team", fields: teamFields, icon: UserCircle },
              ]}
            />
          </TabsContent>

          {/* Newsletters + Content + Image */}
          <TabsContent value="newsletters">
            <PageEditor title="Marketing" token={token}
              contentKeys={[
                { key: "newsletterHeaderDesc", label: "Page Header Description", type: "textarea" },
                { key: "mediaAppLink", label: "Application Link (URL)" },
                { key: "mediaAppDue", label: "Application Due Date" },
              ]}
              imageKeys={[{ key: "newsletterHero", label: "Hero Image" }]}
              crudSections={[
                { title: "Newsletter", resource: "newsletters", fields: newsletterFields, icon: Newspaper },
              ]}
            />
          </TabsContent>

          {/* Case Competition — Content + Images + Documents */}
          <TabsContent value="conference">
            <PageEditor title="Case Competition" token={token}
              contentKeys={[
                { key: "confBadge", label: "Header Badge Text (e.g. 'Competition')" },
                { key: "confName", label: "Competition Name" },
                { key: "confTagline", label: "Tagline", type: "textarea" },
                { key: "confDate", label: "Date" },
                { key: "confTime", label: "Time" },
                { key: "confLocation", label: "Location" },
                { key: "confRegLink", label: "Registration Link (leave empty to hide button)" },
                { key: "confOverviewTitle", label: "Overview Section Title" },
                { key: "confOverviewText", label: "Overview Paragraph 1", type: "textarea" },
                { key: "confOverviewText2", label: "Overview Paragraph 2", type: "textarea" },
                { key: "confDetailsTitle", label: "Details Section Title" },
                { key: "confDetailsFee", label: "Entry Fee (e.g. '$30 per team')" },
                { key: "confDetailsTeamSize", label: "Team Size (e.g. 'Max 4 people per team')" },
                { key: "confDetailsRound1", label: "Round 1 Info (e.g. 'September 26th')" },
                { key: "confDetailsPlacement", label: "Final Placement (e.g. 'October 3rd')" },
                { key: "confDetailsRound2", label: "Round 2 Info (e.g. 'November 14th at McCombs')" },
                { key: "confDetailsPrizes", label: "Prizes (e.g. 'Cash prizes for the winner')" },
                { key: "confDetailsRegistration", label: "Registration Method" },
                { key: "confDocumentsTitle", label: "Documents Section Title" },
                { key: "confDocumentsSubtitle", label: "Documents Section Subtitle", type: "textarea" },
                { key: "confCTATitle", label: "Bottom CTA Title" },
                { key: "confCTAText", label: "Bottom CTA Text", type: "textarea" },
              ]}
              imageKeys={[
                { key: "confHero", label: "Competition Image" },
              ]}
              crudSections={[
                { title: "Document", resource: "case-comp-documents", fields: caseCompDocFields, icon: FileText },
              ]}
            />
          </TabsContent>

          {/* Member Resources — Professional Dev + General Info + Philanthropy */}
          <TabsContent value="member-resources">
            <PageEditor title="Member Resources" token={token}
              contentKeys={[
                { key: "_divider_pd", label: "── Professional Development ──", type: "divider" },
                { key: "pdHeaderDesc", label: "PD: Header Description", type: "textarea" },
                { key: "pdOverviewTitle", label: "PD: Overview Title" },
                { key: "pdOverviewText", label: "PD: Overview Text", type: "textarea" },
                { key: "pdOverviewText2", label: "PD: Overview Text (paragraph 2)", type: "textarea" },
                { key: "pdResourcesTitle", label: "PD: Resources Section Title" },
                { key: "pdResourcesSubtitle", label: "PD: Resources Subtitle", type: "textarea" },
                { key: "pdResource1Title", label: "PD: Resource 1 Title" },
                { key: "pdResource1Desc", label: "PD: Resource 1 Description", type: "textarea" },
                { key: "pdResource2Title", label: "PD: Resource 2 Title" },
                { key: "pdResource2Desc", label: "PD: Resource 2 Description", type: "textarea" },
                { key: "pdResource3Title", label: "PD: Resource 3 Title" },
                { key: "pdResource3Desc", label: "PD: Resource 3 Description", type: "textarea" },
                { key: "pdResource4Title", label: "PD: Resource 4 Title" },
                { key: "pdResource4Desc", label: "PD: Resource 4 Description", type: "textarea" },
                { key: "pdResource5Title", label: "PD: Resource 5 Title" },
                { key: "pdResource5Desc", label: "PD: Resource 5 Description", type: "textarea" },
                { key: "pdCTATitle", label: "PD: CTA Title" },
                { key: "pdCTAText", label: "PD: CTA Text", type: "textarea" },

                { key: "_divider_gm", label: "── General Member Info ──", type: "divider" },
                { key: "gmHeaderDesc", label: "GM: Header Description", type: "textarea" },
                { key: "gmOverviewTitle", label: "GM: Overview Title" },
                { key: "gmOverviewText", label: "GM: Overview Text", type: "textarea" },
                { key: "gmOverviewText2", label: "GM: Overview Text (paragraph 2)", type: "textarea" },
                { key: "gmLinksTitle", label: "GM: Links Section Title" },
                { key: "gmLink1Label", label: "GM: Link 1 — Label" },
                { key: "gmLink1Url", label: "GM: Link 1 — URL" },
                { key: "gmLink1Desc", label: "GM: Link 1 — Description", type: "textarea" },
                { key: "gmLink2Label", label: "GM: Link 2 — Label" },
                { key: "gmLink2Url", label: "GM: Link 2 — URL" },
                { key: "gmLink2Desc", label: "GM: Link 2 — Description", type: "textarea" },
                { key: "gmLink3Label", label: "GM: Link 3 — Label" },
                { key: "gmLink3Url", label: "GM: Link 3 — URL" },
                { key: "gmLink3Desc", label: "GM: Link 3 — Description", type: "textarea" },
                { key: "gmLink4Label", label: "GM: Link 4 — Label" },
                { key: "gmLink4Url", label: "GM: Link 4 — URL" },
                { key: "gmLink4Desc", label: "GM: Link 4 — Description", type: "textarea" },
                { key: "gmLink5Label", label: "GM: Link 5 — Label" },
                { key: "gmLink5Url", label: "GM: Link 5 — URL" },
                { key: "gmLink5Desc", label: "GM: Link 5 — Description", type: "textarea" },
                { key: "gmCTATitle", label: "GM: CTA Title" },
                { key: "gmCTAText", label: "GM: CTA Text", type: "textarea" },
              ]}
              imageKeys={[
                { key: "pdHero", label: "Professional Development Image" },
              ]}
              crudSections={[]}
            />
          </TabsContent>

          {/* Contacts */}
          <TabsContent value="contacts"><ContactsView token={token} /></TabsContent>

          {/* Chatbot Knowledge Base */}
          <TabsContent value="chatbot">
            <PageEditor title="Chatbot Knowledge Base" token={token}
              contentKeys={[]}
              imageKeys={[]}
              crudSections={[
                { title: "Knowledge Item", resource: "chatbot-knowledge", fields: chatbotKnowledgeFields, icon: Mic2 },
              ]}
            />
          </TabsContent>

          {/* Analytics */}
          <TabsContent value="analytics"><AnalyticsDashboard token={token} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
